import { Button, Col, Form, List, Typography } from "antd";
import Icon from "@ant-design/icons";
import { Icons } from "../../assets/icons";
import { useEffect, useState } from "react";
import DynamicForm from "../../controls/DynamicForm";
import { createSearchRequest } from "../../hooks/useSearchRequest";
import {
  getUserLabels,
  addUserLabel,
  deleteUserLabel,
  getPlatformUsers,
} from "../../repository/data.repository";
import { UserLabel, MAX_PAGE_SIZE } from "../../repository/data.default";
import { QueryOperation } from "../../code/search-query/search-query";
import { retry, getProblemMessage } from "../../utils/http";

export interface LabelUsersManagementProps {
  readonly labelUid: string;
  readonly setError: (message: string) => void;
}

/**
 * Manages a label's **individual-user targeting** (the `LabelUser` junction) from the label's
 * perspective: pick a user to add it, remove from the list. The per-user counterpart to
 * LabelRolesManagement — add/remove apply immediately.
 */
function LabelUsersManagement(props: LabelUsersManagementProps) {
  const [userForm] = Form.useForm();
  const [users, setUsers] = useState<UserLabel[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (props.labelUid) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.labelUid]);

  function load(): void {
    setLoading(true);
    retry(() =>
      getUserLabels({
        searchQuery: {
          conditions: [
            { field: "labelUid", operation: QueryOperation.Equal, values: [props.labelUid] },
          ],
        },
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
      }),
    )
      .then((data) => {
        setUsers(data.data ?? []);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        props.setError(getProblemMessage(err));
      });
  }

  function onAddUser(): void {
    const user = userForm.getFieldValue("user");
    if (!user?.value) return;
    props.setError("");
    setLoading(true);
    addUserLabel({ userUid: user.value, labelUids: [props.labelUid] })
      .then(() => {
        userForm.resetFields();
        load();
      })
      .catch((err) => {
        setLoading(false);
        props.setError(getProblemMessage(err));
      });
  }

  function onRemoveUser(user: UserLabel): void {
    props.setError("");
    setLoading(true);
    deleteUserLabel(user.userUid, props.labelUid)
      .then(() => load())
      .catch((err) => {
        setLoading(false);
        props.setError(getProblemMessage(err));
      });
  }

  const usersRequest = createSearchRequest(getPlatformUsers, "userName");

  return (
    <Col className="flex min-w-0 flex-col">
      <Typography.Text strong className="mb-1 block">
        Target Users
      </Typography.Text>
      <Typography.Text type="secondary" className="mb-2 block text-xs">
        Label-based targeting also reaches these specific users.
      </Typography.Text>
      <DynamicForm
        form={userForm}
        schema={{
          groups: [
            {
              controls: [
                {
                  type: "combobox",
                  name: "user",
                  label: "Add a user",
                  request: usersRequest,
                },
              ],
            },
          ],
        }}
        onChange={onAddUser}
      />
      <List
        size="small"
        loading={loading}
        dataSource={users}
        locale={{ emptyText: "No users targeted yet" }}
        renderItem={(user) => (
          <List.Item
            className="px-1"
            actions={[
              <Button
                key="remove"
                type="text"
                danger
                icon={<Icon className="text-xl" component={Icons.CloseIcon} />}
                onClick={() => onRemoveUser(user)}
              />,
            ]}
          >
            <Typography.Text className="truncate">
              {user.userUserName}
            </Typography.Text>
          </List.Item>
        )}
      />
    </Col>
  );
}

export default LabelUsersManagement;
